import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { MonthPicker } from "../../components/shared/MonthPicker";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { toast } from "../../components/shared/Toast";
import { AttendanceOverrideModal } from "../../components/shared/AttendanceOverrideModal";
import { SalaryAdvancesPanel } from "../../components/shared/SalaryAdvancesPanel";
import { useAppData } from "../../lib/AppDataContext";
import {
  ATTENDANCE_STATUS_META, isAutoAbsent,
  parseBillingMonth, daysInMonth, dateKeyFor, formatDurationMs,
} from "../../lib/attendance";

const WEEKDAY_NAMES = ["Sundays","Mondays","Tuesdays","Wednesdays","Thursdays","Fridays","Saturdays"];

/* Attendance Monthly Calendar — Office Timing panel + employees × dates
   grid with admin override on any cell + salary advances panel below. */
export function AttendanceCalendarView() {
  const {
    allSalesPeople, attendanceLog, markAttendance,
    officeTiming, setOfficeTiming,
    month, setMonth,
  } = useAppData();
  // Exclude Settings>Users-deactivated users and super_admins.
  const activeSalesPeople = allSalesPeople.filter(
    (m) => m.userActive !== false && m.role !== "super_admin"
  );

  const { year, month: m } = parseBillingMonth(month);
  const total = daysInMonth(month);
  const days = Array.from({ length: total }, (_, i) => i + 1);
  const [overrideTarget, setOverrideTarget] = useState(null);

  // Office-timing draft state. Pre-populated from root state.
  //
  // AppDataContext loads office timing ASYNCHRONOUSLY, so on a cold mount
  // this useState captured DEFAULT_OFFICE_TIMING rather than the saved
  // values. Changing only the weekend then PATCHed the whole draft --
  // silently overwriting a configured 10:00 start / 30 min grace back to
  // the 09:30 / 15 defaults -- and the calendar below (which reads
  // officeTiming, not the draft) disagreed with the form.
  //
  // Re-sync whenever the context value arrives or changes. After a save
  // the two already match, so this never fights an in-progress edit.
  const [timingDraft, setTimingDraft] = useState(officeTiming);
  useEffect(() => { setTimingDraft(officeTiming); }, [officeTiming]);
  const [timingKeep, setTimingKeep] = useState(true);
  const timingChanged =
    timingDraft.startHHMM !== officeTiming.startHHMM ||
    timingDraft.endHHMM !== officeTiming.endHHMM ||
    timingDraft.graceMinutes !== officeTiming.graceMinutes ||
    (timingDraft.weekendDay ?? 0) !== (officeTiming.weekendDay ?? 0);
  const saveTiming = () => { setOfficeTiming(timingDraft); toast("Office timing updated"); };
  const resetTiming = () => setTimingDraft(officeTiming);

  const cellFor = (name, day) => {
    const dKey = dateKeyFor(month, day);
    return (attendanceLog[name] || []).find((r) => r.date === dKey);
  };

  return (
    <div>
      <PageHeader accent="ops" title="Attendance · Monthly calendar" subtitle={`${month} · click any cell to override`} />

      {/* Office Timing panel */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Office timing</h3>
            <p className="text-xs text-slate-500 mt-0.5">Clock-ins later than start + grace are marked Half Day automatically.</p>
          </div>
          {timingChanged && <Badge cls="bg-blue-50 text-blue-700 ring-blue-600/20">Unsaved changes</Badge>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Start time</label>
            <input type="time" value={timingDraft.startHHMM}
              onChange={(e) => setTimingDraft({ ...timingDraft, startHHMM: e.target.value })}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500 tabular-nums" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">End time</label>
            <input type="time" value={timingDraft.endHHMM}
              onChange={(e) => setTimingDraft({ ...timingDraft, endHHMM: e.target.value })}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500 tabular-nums" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Grace (minutes)</label>
            <input type="number" min="0" max="120" value={timingDraft.graceMinutes}
              onChange={(e) => setTimingDraft({ ...timingDraft, graceMinutes: Math.max(0, Math.min(120, parseInt(e.target.value) || 0)) })}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500 tabular-nums" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Weekend</label>
            <select value={timingDraft.weekendDay ?? 0}
              onChange={(e) => setTimingDraft({ ...timingDraft, weekendDay: parseInt(e.target.value) })}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-2.5 py-2 bg-white focus:outline-none focus:ring-blue-500">
              <option value={0}>Sunday</option>
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="flex gap-2 w-full">
              <Btn variant="primary" size="sm" onClick={saveTiming} disabled={!timingChanged} className="flex-1">
                <Check className="w-3.5 h-3.5" />Save
              </Btn>
              {timingChanged && (
                <Btn variant="ghost" size="sm" onClick={resetTiming}>Reset</Btn>
              )}
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={timingKeep}
            onChange={(e) => setTimingKeep(e.target.checked)}
            className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500" />
          <span>
            Keep this timing until changes are made
            {timingKeep && (
              <span className="text-slate-400">
                {" "}· current: <span className="font-medium text-slate-700 tabular-nums">
                  {officeTiming.startHHMM} – {officeTiming.endHHMM} ({officeTiming.graceMinutes} min grace)
                </span>
              </span>
            )}
          </span>
        </label>
      </Card>

      {/* Calendar grid */}
      <Card>
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">Month</span>
          <MonthPicker value={month} onChange={setMonth} />
          <span className="text-xs text-slate-400 ml-auto">
            {WEEKDAY_NAMES[officeTiming.weekendDay ?? 0]} shaded · Tap a cell to set status
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-left text-slate-500 font-medium sticky left-0 bg-slate-50 z-10 min-w-[160px]">Person</th>
                {days.map((d) => {
                  const dayDate = new Date(year, m, d);
                  const isWeekend = dayDate.getDay() === (officeTiming.weekendDay ?? 0);
                  const weekday = dayDate.toLocaleDateString("en-US", { weekday: "short" });
                  return (
                    <th key={d}
                      className={`px-1 py-2 font-medium tabular-nums leading-tight ${isWeekend ? "bg-slate-200 text-slate-500" : "text-slate-500"}`}
                      style={{ minWidth: 32 }}>
                      <div className="text-[9px] font-semibold uppercase tracking-tight">{weekday}</div>
                      <div className="text-[11px]">{d}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {activeSalesPeople.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="px-3 py-2 text-slate-900 font-medium sticky left-0 bg-white z-10">{p.name}</td>
                  {days.map((d) => {
                    const rec = cellFor(p.name, d);
                    /* A "weekend" record is this employee's own day off, from a
                       launch-week swap, and it reads exactly like a company
                       weekend: shaded and blank, no chip. Same branch as the
                       calendar weekend so the two cannot look different.

                       The cell stays clickable, so a mistaken mark can be
                       changed back. */
                    const isWeekend =
                      new Date(year, m, d).getDay() === (officeTiming.weekendDay ?? 0) ||
                      rec?.status === "weekend";
                    const dKey = dateKeyFor(month, d);
                    const auto = !rec && isAutoAbsent(dKey, officeTiming.weekendDay ?? 0, officeTiming.endHHMM, p.profile?.joiningDate);
                    const meta =
                      rec?.status === "weekend" ? null
                      : rec ? ATTENDANCE_STATUS_META[rec.status]
                      : (auto ? ATTENDANCE_STATUS_META.absent : null);
                    return (
                      <td key={d} className={`p-0.5 ${isWeekend ? "bg-slate-100" : ""}`}>
                        <button
                          onClick={() => setOverrideTarget({ name: p.name, date: dKey, existing: rec })}
                          title={meta ? (() => {
                            const total = rec?.clockInAt && rec?.clockOutAt
                              ? formatDurationMs(new Date(rec.clockOutAt) - new Date(rec.clockInAt)) : null;
                            const parts = [meta.label];
                            if (rec?.clockInTime) parts.push("In " + rec.clockInTime);
                            if (rec?.clockOutTime) parts.push("Out " + rec.clockOutTime);
                            if (total) parts.push("Total " + total);
                            return parts.join(" · ");
                          })() : "Not marked"}
                          className={`w-7 h-7 rounded grid place-items-center text-[10px] font-bold hover:ring-2 hover:ring-blue-400 transition-all ${meta ? meta.cls : "bg-white ring-1 ring-slate-200 text-slate-300"}`}>
                          {meta ? meta.label[0] : "·"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap gap-3 text-xs">
          {Object.entries(ATTENDANCE_STATUS_META).map(([k, v]) => (
            <span key={k} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded ring-1 ring-inset ${v.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${v.dot}`} />{v.label}
            </span>
          ))}
        </div>
      </Card>

      {/* Salary advances panel sits right below */}
      <SalaryAdvancesPanel />

      <AttendanceOverrideModal
        target={overrideTarget}
        onMark={markAttendance}
        onClose={() => setOverrideTarget(null)}
      />
    </div>
  );
}
